import React, { useState, useMemo, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";

const ACTION_TABS = [
  { key: "all", label: "All" },
  { key: "view", label: "View" },
  { key: "add", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
  { key: "exports", label: "Export" },
];

const PermissionModal = ({
  show,
  onHide,
  role,
  allPermissions = [],
  permissionsLoading,
  onSave,
  onFilterChange, // NEW
}) => {
  const [selected, setSelected] = useState(
    role?.permissions?.map((p) => ({ _id: p._id, permissionKey: p.permissionKey })) || []
  );
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeAction, setActiveAction] = useState("all"); // NEW

  const selectedIds = useMemo(() => selected.map((p) => p._id), [selected]);

  // Re-fetch from the API whenever the action tab changes (while modal is open)
  useEffect(() => {
    if (show && onFilterChange) {
      onFilterChange(activeAction);
    }
  }, [activeAction, show]);

  const availableToAdd = useMemo(() => {
    return allPermissions.filter(
      (p) =>
        !selectedIds.includes(p._id) &&
        p.permissionKey.toLowerCase().includes(search.toLowerCase())
    );
  }, [allPermissions, selectedIds, search]);

  const handleRemove = (id) => {
    setSelected((prev) => prev.filter((p) => p._id !== id));
  };

  const handleAdd = (perm) => {
    setSelected((prev) => [...prev, { _id: perm._id, permissionKey: perm.permissionKey }]);
    setSearch("");
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(role._id, selectedIds);
      onHide();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="permission-modal-wide">
      <style>{`
        .permission-modal-wide {
          max-width: 850px;
          width: 90%;
        }
      `}</style>

      <Modal.Header closeButton>
        <Modal.Title>
          Manage Permissions {role?.roleName ? `— ${role.roleName}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <label style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>
          Permissions
        </label>

        {/* Selected permission pills */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {selected.length === 0 && <span className="text-muted">No permissions selected</span>}
          {selected.map((p) => (
            <Badge
              key={p._id}
              pill
              style={{
                backgroundColor: "#7c6ff0",
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                fontSize: "13px",
              }}
            >
              {p.permissionKey}
              <span
                style={{ cursor: "pointer", marginLeft: 4 }}
                onClick={() => handleRemove(p._id)}
              >
                ×
              </span>
            </Badge>
          ))}
        </div>

        {/* Action filter tabs — NEW */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          {ACTION_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveAction(tab.key)}
              style={{
                border: "1px solid #7c6ff0",
                background: activeAction === tab.key ? "#7c6ff0" : "#fff",
                color: activeAction === tab.key ? "#fff" : "#7c6ff0",
                borderRadius: 20,
                padding: "4px 14px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search + select */}
        <input
          type="text"
          className="form-control"
          placeholder="Search and select permissions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 8 }}
        />

        <div
          style={{
            maxHeight: 220,
            overflowY: "auto",
            border: "1px solid #eee",
            borderRadius: 6,
          }}
        >
          {permissionsLoading && (
            <div style={{ padding: 10, color: "#999" }}>Loading permissions...</div>
          )}
          {!permissionsLoading && availableToAdd.length === 0 && (
            <div style={{ padding: 10, color: "#999" }}>No matching permissions</div>
          )}
          {!permissionsLoading &&
            availableToAdd.map((p) => (
              <div
                key={p._id}
                onClick={() => handleAdd(p)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderLeft: "3px solid #7c6ff0",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5ff")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <strong>{p.permissionKey}</strong>{" "}
                <span style={{ color: "#999" }}>
                  ({p.module} / {p.action})
                </span>
              </div>
            ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PermissionModal;